import { useEmbedStore } from '@/stores/embed-store';

// ---- Message type constants ----

/** Actions sent TO scio3 parent (child → parent) */
export const CHILD_ACTION = {
  ON_INIT: 'ON_INIT',
  ON_GET_TOKEN: 'ON_GET_TOKEN',
  NAVIGATE: 'NAVIGATE',
  OPEN_BILLING: 'OPEN_BILLING',
  PUBLISH_COMPLETE: 'PUBLISH_COMPLETE',
  RESIZE: 'RESIZE',
  /** Request scio3 to provision an OptiDev workspace via OptiPrime API.
   *  Response: { ok: boolean; workspaceId?: string; error?: string }
   *  scio3 also pushes WORKSPACE_READY after provisioning succeeds. */
  PROVISION_WORKSPACE: 'PROVISION_WORKSPACE',
} as const;

// ---- Payload types ----

export interface SyncAuthPayload {
  token: string;
  uid: string;
}

export interface SyncTeamPayload {
  teamId: string;
  name: string;
}

export interface NavigateToPayload {
  path: string;
}

export interface SyncThemePayload {
  theme: 'light' | 'dark';
}

export interface PublishCompletePayload {
  projectId: string;
  deployUrl: string;
  projectName: string;
  openPushToScreens?: boolean;
}

/**
 * Incoming message from scio3 parent.
 * Uses the standard scio3 bridge protocol: { action, requestId, data }
 * Sent as JSON string via postMessage.
 */
interface ParentMessage {
  action: string;
  requestId?: string;
  data?: unknown;
}

type ActionHandler = (data: unknown) => void;

const ALLOWED_ORIGINS = [
  /^https:\/\/.*\.optisigns\.com$/,
  /^http:\/\/localhost:\d+$/,
];

function isAllowedOrigin(origin: string): boolean {
  return ALLOWED_ORIGINS.some((pattern) => pattern.test(origin));
}

let requestIdCounter = 0;

/**
 * OptiSignsBridge — child-side postMessage bridge for scio3 embed.
 *
 * Follows the scio3 bridge protocol (same as Designer/scio1):
 * - Child → Parent: JSON.stringify({ action, requestId, data })
 * - Parent → Child: JSON.stringify({ action: 'responsePromise', requestId, data })
 *
 * Supports fire-and-forget AND request-response via sendRequest().
 */
export class OptiSignsBridge {
  private handlers = new Map<string, ActionHandler[]>();
  private boundListener: ((e: MessageEvent) => void) | null = null;
  /** Pending request-response resolvers keyed by requestId */
  private pendingRequests = new Map<string, (data: unknown) => void>();

  /** Register a handler for a parent action */
  on(action: string, handler: ActionHandler): this {
    const list = this.handlers.get(action) ?? [];
    list.push(handler);
    this.handlers.set(action, list);
    return this;
  }

  /** Start listening for parent messages */
  init(): void {
    this.boundListener = this.handleMessage.bind(this);
    window.addEventListener('message', this.boundListener);
  }

  /** Stop listening */
  destroy(): void {
    if (this.boundListener) {
      window.removeEventListener('message', this.boundListener);
      this.boundListener = null;
    }
    this.handlers.clear();
    this.pendingRequests.clear();
  }

  /** Send a fire-and-forget message to the parent window */
  sendToParent(action: string, data?: unknown): void {
    if (!window.parent || window.parent === window) return;

    const parentOrigin = useEmbedStore.getState().parentOrigin;
    const targetOrigin = parentOrigin ?? '*';

    window.parent.postMessage(
      JSON.stringify({ action, data }),
      targetOrigin,
    );
  }

  /**
   * Send a request to the parent and wait for a responsePromise reply.
   * Same protocol as Designer ↔ scio3 bridge.
   */
  sendRequest<T = unknown>(action: string, data?: unknown): Promise<T> {
    return new Promise((resolve, reject) => {
      const requestId = `optidev-${++requestIdCounter}`;

      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Request ${action} timed out (requestId: ${requestId})`));
      }, 10_000);

      this.pendingRequests.set(requestId, (responseData: unknown) => {
        clearTimeout(timeout);
        resolve(responseData as T);
      });

      if (!window.parent || window.parent === window) {
        clearTimeout(timeout);
        this.pendingRequests.delete(requestId);
        reject(new Error('No parent window'));
        return;
      }

      const parentOrigin = useEmbedStore.getState().parentOrigin;
      const targetOrigin = parentOrigin ?? '*';

      window.parent.postMessage(
        JSON.stringify({ action, requestId, data }),
        targetOrigin,
      );
    });
  }

  private handleMessage(event: MessageEvent): void {
    // Validate origin
    if (!isAllowedOrigin(event.origin)) return;

    // Parse JSON string (scio3 protocol sends stringified JSON)
    let msg: ParentMessage;
    if (typeof event.data === 'string') {
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
    } else if (typeof event.data === 'object' && event.data?.action) {
      msg = event.data;
    } else {
      return;
    }

    if (!msg.action) return;

    // Store parent origin on first valid message
    const store = useEmbedStore.getState();
    if (!store.parentOrigin) {
      store.setParentOrigin(event.origin);
    }

    // Handle responsePromise — resolve pending request
    if (msg.action === 'responsePromise' && msg.requestId) {
      const resolver = this.pendingRequests.get(msg.requestId);
      if (resolver) {
        this.pendingRequests.delete(msg.requestId);
        resolver(msg.data);
      }
      return;
    }

    // Dispatch to registered action handlers
    const handlers = this.handlers.get(msg.action);
    if (handlers) {
      for (const handler of handlers) {
        handler(msg.data);
      }
    }
  }
}

/** Singleton bridge instance — set by useOptiSignsBridge, used by auth.getIdToken() */
let activeBridge: OptiSignsBridge | null = null;

export function setActiveBridge(bridge: OptiSignsBridge | null): void {
  activeBridge = bridge;
}

export function getActiveBridge(): OptiSignsBridge | null {
  return activeBridge;
}
